// src/services/questionnaires.service.js
import { http } from "./http";

const API_URL = process.env.REACT_APP_API_URL;

/* ---------- helpers ---------- */
function nonEmptyArray(arr) {
  return Array.isArray(arr) && arr.length > 0 ? arr : undefined;
}

/**
 * Existing helper used by create/update.
 * NOTE: kept as-is for other callers, but it uses "questionnaireDefault".
 */
function buildPayload(dto = {}) {
  const {
    name,
    roles,
    rolesIds,
    departments,
    departmentIds,
    users,
    questions,
    questionIds,
    questionnaireDefault,
    defaultAssignment,
    admin,
    processInstanceId,
    active,
    status,
  } = dto;

  const normRoles =
    roles ?? nonEmptyArray(rolesIds)?.map((id) => ({ id }));

  const normDepartments =
    departments ?? nonEmptyArray(departmentIds)?.map((id) => ({ id }));

  const normQuestions =
    questions ??
    nonEmptyArray(questionIds)?.map((id, position) => ({
      id,
      position,
      required: true,
    }));

  return {
    ...(name !== undefined ? { name } : {}),
    ...(nonEmptyArray(normRoles) ? { roles: normRoles } : {}),
    ...(nonEmptyArray(normDepartments) ? { departments: normDepartments } : {}),
    ...(nonEmptyArray(users) ? { users } : {}),
    ...(Array.isArray(normQuestions) ? { questions: normQuestions } : {}),
    ...(questionnaireDefault !== undefined ? { questionnaireDefault } : {}),
    ...(defaultAssignment !== undefined ? { defaultAssignment } : {}),
    ...(admin !== undefined ? { admin } : {}),
    ...(processInstanceId !== undefined ? { processInstanceId } : {}),
    ...(active !== undefined ? { active } : {}),
    ...(status !== undefined ? { status } : {}),
  };
}

/** List of questionnaires */
export async function listQuestionnaires() {
  const { data } = await http.get(`${API_URL}/api/questionnaires`);
  return data;
}

/** Get a questionnaire */
export async function getQuestionnaire(id) {
  const { data } = await http.get(`${API_URL}/api/questionnaires/${id}`);
  return data;
}

/** Create questionnaire */
export async function createQuestionnaire(dto) {
  const payload = buildPayload(dto);
  if (!payload.name) throw new Error("createQuestionnaire: 'name' is required");
  const { data } = await http.post(
    `${API_URL}/api/questionnaires`,
    payload
  );
  return data;
}

/** Update questionnaire (generic, legacy mapping) */
export async function updateQuestionnaire(id, dto) {
  if (!id) throw new Error("updateQuestionnaire: 'id' is required");
  const payload = buildPayload(dto);
  const { data } = await http.put(
    `${API_URL}/api/questionnaires/${id}`,
    payload
  );
  return data;
}

export async function setQuestionnaireActive(id, active) {
  if (!id || typeof active !== "boolean") throw new Error("El cuestionario y su estado son obligatorios.");
  const { data } = await http.patch(
    `${API_URL}/api/questionnaires/${encodeURIComponent(id)}/active`, { active }
  );
  return data;
}

/**
 * Update while preserving existing questions and allowing
 * `defaultAssignment`, `admin`, `active` and `status` exactly as backend expects.
 *
 * opts:
 *  - defaultAssignment?: boolean
 *  - adminId?: string | null  (null para limpiar admin)
 *  - active?: boolean
 *  - status?: string
 */
export async function updateQuestionnairePreserveQuestions(id, opts = {}) {
  if (!id)
    throw new Error(
      "updateQuestionnairePreserveQuestions: 'id' is required"
    );

  // 1) fetch current questionnaire
  const current = await getQuestionnaire(id);
  const qName = current?.name;
  if (!qName) throw new Error("El cuestionario no tiene nombre");

  // 2) normalize existing questions to the exact schema the backend wants
  const normalizedQuestions = (Array.isArray(current?.questions)
    ? current.questions
    : []
  )
    .filter((q) => q && q.id)
    .map((q, i) => ({
      id: q.id,
      text: typeof q.text === "string" ? q.text : "",
      position: Number.isFinite(q.position) ? q.position : i,
      required: q.required !== undefined ? !!q.required : true,
    }));

  // 3) build payload explicitly so we don’t drop fields accidentally
  const payload = {
    name: qName,
    questions: normalizedQuestions,
    ...(opts.defaultAssignment !== undefined
      ? { defaultAssignment: !!opts.defaultAssignment }
      : {}),
    ...(opts.adminId !== undefined
      ? opts.adminId
        ? { admin: { id: String(opts.adminId) } }
        : { admin: null }
      : {}),
    ...(opts.active !== undefined ? { active: !!opts.active } : {}),
    ...(opts.status !== undefined ? { status: opts.status } : {}),
    // Importante: NO mandamos questionnaireDefault aquí.
  };

  const { data } = await http.put(
    `${API_URL}/api/questionnaires/${id}`,
    payload
  );
  return data;
}

/** Send assignments to Camunda (ruta antigua, por si la usas en otro lado) */
export async function sendQuestionnaireAssignments(id, targets) {
  // targets: { roles?:[{id}], departments?:[{id}], users?:[{id}] }
  const { data } = await http.post(
    `${API_URL}/api/questionnaires/send/${id}`,
    targets
  );
  return data;
}

/** Duplicate questionnaire */
export async function duplicateQuestionnaire(id) {
  const { data } = await http.post(
    `${API_URL}/api/questionnaires/duplicate/${id}`
  );
  return data;
}

export async function deleteQuestionnaire(id) {
  if (!id) throw new Error("deleteQuestionnaire: 'id' is required");
  const res = await http.delete(
    `${API_URL}/api/questionnaires/${id}`
  );
  // backend may return 204 No Content — normalize the return
  return res.status === 204 ? null : res.data;
}
